'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useNode } from '@craftjs/core';
import { supabase } from '@/lib/supabase';
import { useEnsureVehicles } from '@/hooks/useEnsureVehicles';
import useClientStore from '@/store/useClientStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { isBrasil, forcedSiteLanguage } from '@/lib/country';
import { textoEncontrados } from '@/lib/textos-usa';
import { etiquetaCatalogo } from '@/utils/functions';
import { useCurrency } from '@/hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { VehicleList } from './VehicleList';
import { getVehicleTypeIcon } from './vehicleTypeIcons';
import { latestEventDate, passesRecencyWindow } from '@/utils/webVisibility';
import { htmlSeguro } from '../../utils/htmlSeguro';
import {
  ORDEN_INICIAL,
  ordenDelTenant,
  ordenarVehiculos,
  type OrdenVehiculos,
} from '@/lib/orden-vehiculos';

type CraftComponent<P = {}> = React.FC<P> & { craft?: any };

type StatusName = 'Publicado' | 'Vendido' | 'Reservado';
type VehicleRel = { id?: number; name?: string; show_in_web?: boolean };

// 🔸 Literal permitido por VehicleList
type FeatureKey = 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';

type VehicleRow = {
  id: number;
  year?: number;
  price?: number;
  mileage?: number;
  transmission?: string | null;
  main_image?: string | null;
  discount_percentage?: number | null;
  created_at?: string | null;
  state_updated_at?: string | null;
  status?: VehicleRel & { name?: StatusName | string; web_visibility_days?: number | null };
  brand?: VehicleRel;
  model?: VehicleRel;
  category?: VehicleRel;
  fuel_type?: VehicleRel;
  condition?: VehicleRel;
  color?: VehicleRel;
  vehicles_sales?:
    | Array<{ created_at: string; is_historical_import?: boolean | null }>
    | { created_at: string; is_historical_import?: boolean | null }
    | null;
  vehicles_reservations?:
    | Array<{ created_at: string; is_historical_import?: boolean | null }>
    | { created_at: string; is_historical_import?: boolean | null }
    | null;
  is_historical_import?: boolean | null;
  event_date?: string;
};

type GradientCfg = {
  from: string;
  via: string;
  to: string;
  angle?: number; // grados
};

interface Props {
  title?: string;
  subtitle?: string;
  bgColor?: string;
  textColor?: string;
  columns?: 2 | 3 | 4;
  showStatuses?: StatusName[];
  gradient?: GradientCfg; // 👈 NUEVO: gradient configurable
  cardSettings?: {
    cardBgColor: string;
    cardBorderColor: string;
    cardTextColor: string;
    cardPriceColor: string;
    cardButtonColor: string;
    cardButtonTextColor: string;
    detailsButtonText: string;
    bannerPosition: 'left' | 'right';
    pricePosition: 'overlay' | 'below-title';
    featuresConfig?: {
      feature1?: FeatureKey;
      feature2?: FeatureKey;
      feature3?: FeatureKey;
      feature4?: FeatureKey;
    };
  }[];
  newBadgeText?: string;
  children?: React.ReactNode;
}

/**
 * Red de seguridad: si alguna vez un icono no cargara, se oculta en vez de
 * dejar el marco de imagen rota. El nombre del tipo ya va en el <span> de abajo.
 */
const hideBrokenIcon = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.display = 'none';
};

export const VehicleGrid2: CraftComponent<Props> = ({
  title = 'Sarret Cars Boutique',
  subtitle = '',
  bgColor = '#ffffff',
  textColor = '#111827',
  columns = 3,
  showStatuses = ['Publicado', 'Reservado', 'Vendido'],
  gradient = { from: '#000000', via: '#171717', to: '#404040', angle: 135 }, // 👈 default compartido
  cardSettings = [
    {
      cardBgColor: '#ffffff',
      cardBorderColor: '#e5e7eb',
      cardTextColor: '#1f2937',
      cardPriceColor: '#ffffff',
      cardButtonColor: '#224887',
      cardButtonTextColor: '#ffffff',
      detailsButtonText: 'Ver detalles',
      bannerPosition: 'right',
      pricePosition: 'overlay',
      featuresConfig: { feature1: 'category', feature2: 'year', feature3: 'fuel', feature4: 'mileage' },
    },
  ],
  newBadgeText = 'Recién publicado',
  children,
}) => {
  const {
    connectors: { connect: connectRef },
  } = useNode();

  const { client } = useClientStore();
  // Ticket #164: orden inicial que configuró la automotora.
  const ordenTenant = ordenDelTenant((client as Record<string, unknown> | null)?.web_vehicle_order);

  const { formatPrice } = useCurrency();
  const { t } = useTranslation();
  // 'Todos'/'Todas' de abajo son VALORES (estado de los filtros), no texto: lo
  // que se traduce es solo la etiqueta. "Moto, ATV y UTV" es una categoría
  // chilena que se agrega aunque no venga de la base; en Brasil no se inventa.
  const esBrasil = isBrasil(client?.country);
  // EE.UU.: igual que Brasil (etiquetas traducidas, valores de siempre) y sin la
  // categoría chilena inventada.
  const esUsa = forcedSiteLanguage(client?.country) === 'en';
  const etiquetaTodos = t('site.vehicles.allMasc');
  const etiquetaTodas = t('site.vehicles.allFem');
  /** CL: `<option>` sin value (el texto ES el valor, como siempre). BR y US: el
   *  valor explícito, porque el texto visible ya no coincide con el valor. Lo
   *  mismo en Chile si el visitante eligió inglés ('All' ≠ 'Todos'): sin value
   *  el filtro quedaría en 'All' y la grilla vacía. */
  const valorExplicito = (valor: string, etiqueta: string) =>
    esBrasil || esUsa || etiqueta !== valor ? { value: valor } : {};
  /** Nombre VISIBLE de un tipo de la base (en español): en EE.UU., en inglés.
   *  El valor (estado del filtro) sigue siendo el de la base. */
  const nombreTipo = (n: string) => (esUsa ? etiquetaCatalogo('category', n, client?.country, n) : n);

  // ---------------------------
  // TÍTULO: toma de clients.name
  // ---------------------------
  const PLACEHOLDER_TITLE = 'Sarret Cars Boutique';
  const [resolvedTitle, setResolvedTitle] = useState<string>(title);

  useEffect(() => {
    setResolvedTitle(title);
  }, [title]);

  useEffect(() => {
    const shouldResolveFromClient = !title || title.trim() === '' || title === PLACEHOLDER_TITLE;
    if (!shouldResolveFromClient) return;
    if (!client?.id) return;

    const candidate = (client as any)?.name;
    if (candidate && String(candidate).trim() !== '') {
      setResolvedTitle(String(candidate).trim());
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('name')
          .eq('id', +client.id)
          .single();

        if (!error && data?.name) {
          setResolvedTitle(String(data.name).trim());
        }
      } catch (e) {
        console.error('Error resolviendo título desde clients.name:', e);
      }
    })();
  }, [client?.id, title]);

  // data
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [filtered, setFiltered] = useState<VehicleRow[]>([]);
  const [loading, setLoading] = useState(true);

  // options
  const [brands, setBrands] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [fuels, setFuels] = useState<string[]>([]);
  const [trans, setTrans] = useState<string[]>([]);
  const [minMaxPrice, setMinMaxPrice] = useState({ min: 0, max: 1000000000 });
  const [minMaxYear, setMinMaxYear] = useState({ min: 1990, max: new Date().getFullYear() });

  // filters
  const [typeSel, setTypeSel] = useState('Todos');
  const [brandSel, setBrandSel] = useState('Todas');
  const [transSel, setTransSel] = useState('Todas');
  const [fuelSel, setFuelSel] = useState('Todos');
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(1000000000);
  const [yearMin, setYearMin] = useState(1990);
  const [yearMax, setYearMax] = useState(new Date().getFullYear());

  // sort — arranca en el orden INICIAL que configuró la automotora
  // (ticket #164). Antes arrancaba fijo en 'date_desc'.
  const [sortOrder, setSortOrder] = useState<OrdenVehiculos>(ORDEN_INICIAL);

  const hasActive =
    typeSel !== 'Todos' ||
    brandSel !== 'Todas' ||
    transSel !== 'Todas' ||
    fuelSel !== 'Todos' ||
    priceMin !== minMaxPrice.min ||
    priceMax !== minMaxPrice.max ||
    yearMin !== minMaxYear.min ||
    yearMax !== minMaxYear.max;

  // Inventario del tenant, compartido con el resto del sitio (ver
  // useVehiclesStore): el store baja una vez el inventario crudo y acá se le
  // aplican las mismas reglas de siempre. Antes este componente hacía su
  // propia consulta a `vehicles`, idéntica a la del store.
  const {
    rawVehicles: inventario,
    isLoading: inventarioCargando,
    loadedForClientId,
  } = useEnsureVehicles();
  const inventarioListo = !inventarioCargando && String(loadedForClientId) === String(client?.id);

  useEffect(() => {
    const run = () => {
      if (!client?.id) return;
      try {
        const data = inventario.filter((v) => String(v.client_id) === String(client.id));

        // Fecha del evento (venta/reserva con fallback a state_updated_at /
        // updated_at) para Vendido/Reservado
        const processed: VehicleRow[] = (data || []).map((v: any) => ({
          ...v,
          event_date: latestEventDate(v),
        }));

        // Filter vehicles that should be visible on web
        // Use show_in_web field if defined, fallback to name-based logic for backward compatibility
        const byStatus = processed.filter((v) => {
          if (!v.status) return false;
          // If show_in_web is explicitly set, use it
          if (typeof v.status.show_in_web === 'boolean') {
            return v.status.show_in_web;
          }
          // Fallback: use name-based filtering for legacy states without show_in_web
          return showStatuses.includes((v.status.name || '') as StatusName);
        });

        // Ordena Publicado primero y luego por fecha desc
        const sorted = [...byStatus].sort((a, b) => {
          if (a.status?.name === 'Publicado' && b.status?.name !== 'Publicado') return -1;
          if (a.status?.name !== 'Publicado' && b.status?.name === 'Publicado') return 1;
          return +new Date(b.created_at || 0) - +new Date(a.created_at || 0);
        });

        setVehicles(sorted);
        setFiltered(sorted);

        // Opciones para filtros
        const b = [...new Set(sorted.map((v) => v.brand?.name).filter(Boolean))] as string[];
        const t = [...new Set(sorted.map((v) => v.category?.name).filter(Boolean))] as string[];
        const f = [...new Set(sorted.map((v) => v.fuel_type?.name).filter(Boolean))] as string[];
        const tr = [...new Set(sorted.map((v) => v.transmission).filter(Boolean))] as string[];

        // 👉 Igual que en VehicleGrid: min/max de PRECIO solo con disponibles (no R/V)
        const available = sorted.filter(
          (v) => v.status?.name !== 'Vendido' && v.status?.name !== 'Reservado'
        );
        const pricedAvail = available.filter((v) => typeof v.price === 'number') as Array<
          Required<Pick<VehicleRow, 'price'>>
        >;
        const minP = 0;
        const maxP = pricedAvail.length ? Math.max(...pricedAvail.map((v) => v.price)) : 1000000000;

        const years = sorted.map((v) => v.year).filter((y): y is number => !!y);
        const minY = years.length ? Math.min(...years) : 1990;
        const maxY = years.length ? Math.max(...years) : new Date().getFullYear();

        setBrands(b);
        setTypes(t);
        setFuels(f);
        setTrans(tr);

        setMinMaxPrice({ min: minP, max: maxP });
        setPriceMin(minP);
        setPriceMax(maxP);

        setMinMaxYear({ min: minY, max: maxY });
        setYearMin(minY);
        setYearMax(maxY);
      } catch (e) {
        console.error('VehicleGrid2 fetch error:', e);
        setVehicles([]);
        setFiltered([]);
      } finally {
        setLoading(false);
      }
    };
    if (!client?.id) return;
    if (!inventarioListo) {
      setLoading(true);
      return;
    }
    run();
  }, [client?.id, showStatuses, inventario, inventarioListo]);

  // apply filters + sort
  useEffect(() => {
    if (!vehicles.length) return;

    let f = [...vehicles];

    // (A) Filtros básicos
    if (typeSel !== 'Todos') f = f.filter((v) => v.category?.name === typeSel);
    if (brandSel !== 'Todas') f = f.filter((v) => v.brand?.name === brandSel);
    if (transSel !== 'Todas') f = f.filter((v) => (v.transmission || '') === transSel);
    if (fuelSel !== 'Todos') f = f.filter((v) => v.fuel_type?.name === fuelSel);

    // (B) Precio — APLICA A TODOS (incluye Reservado/Vendido)
    f = f.filter((v) => v.price !== undefined && v.price >= priceMin && v.price <= priceMax);

    // (C) Año
    f = f.filter((v) => (v.year ?? 0) >= yearMin && (v.year ?? 0) <= yearMax);

    // (D) Recencia R/V: web_visibility_days del estado, o 3 días por defecto
    f = f.filter((v) => passesRecencyWindow(v));

    // (E) Ordenamiento: una sola implementación para todo el sitio
    // (`lib/orden-vehiculos`), descuento incluido. En 'default' manda el
    // orden que configuró la automotora (ticket #164); con un orden elegido por
    // el visitante, gana el suyo.
    f = ordenarVehiculos(f, sortOrder, ordenTenant);

    setFiltered(f);
    // `ordenTenant` hidrata después del primer render; sin él la grilla se
    // queda con el default "newest".
  }, [vehicles, typeSel, brandSel, transSel, fuelSel, priceMin, priceMax, yearMin, yearMax, sortOrder, ordenTenant]);

  // 🔸 Normalizador de features
  const normalizeFeature = (v: string | undefined): FeatureKey | undefined => {
    const allowed: FeatureKey[] = ['category', 'year', 'fuel', 'mileage', 'transmission'];
    return v && (allowed as string[]).includes(v) ? (v as FeatureKey) : undefined;
  };

  // mapea cardSettings a la forma segura que espera VehicleList
  const safeCardSettings = useMemo(
    () =>
      cardSettings?.map((cs) => ({
        ...cs,
        featuresConfig: cs.featuresConfig && {
          feature1: normalizeFeature(cs.featuresConfig.feature1),
          feature2: normalizeFeature(cs.featuresConfig.feature2),
          feature3: normalizeFeature(cs.featuresConfig.feature3),
          feature4: normalizeFeature(cs.featuresConfig.feature4),
        },
      })),
    [cardSettings]
  );

  const typeCards = useMemo(() => {
    const ciMap = new Map<string, string>();
    types.filter(Boolean).forEach((t) => {
      const key = String(t).toLowerCase();
      if (!ciMap.has(key)) ciMap.set(key, t as string);
    });
    let dbTypes = Array.from(ciMap.values());
    if (!esBrasil && !esUsa && !dbTypes.some((t) => (t || '').toUpperCase() === 'MOTO, ATV Y UTV')) {
      dbTypes.push('Moto, ATV y UTV');
    }
    const withAll = ['Todos', ...dbTypes];
    return withAll.map((t) => {
      if (t === 'Todos') return { name: t, img: undefined as string | undefined };
      return { name: t, img: getVehicleTypeIcon(t) };
    });
  }, [types, esBrasil, esUsa]);

  const getStatusColor = (s: string) => {
    switch (s) {
      case 'Publicado': return 'bg-green-100 text-green-800';
      case 'Vendido': return 'bg-red-100 text-red-800';
      case 'Reservado': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helpers brillo PNG y “Todos”
  const pngGlow = (active: boolean) =>
    [
      'max-h-14 object-contain transition-transform duration-200',
      active
        ? 'drop-shadow-[0_0_22px_rgba(255,255,255,0.55)] scale-[1.03]'
        : 'hover:drop-shadow-[0_0_18px_rgba(255,255,255,0.35)] hover:scale-[1.02]',
    ].join(' ');

  const allPillClasses = (active: boolean, size: 'sm' | 'md') =>
    [
      'rounded-full flex items-center justify-center font-semibold select-none',
      size === 'sm' ? 'min-w-[5.75rem] h-9 px-4 text-[11px]' : 'min-w-[6.25rem] h-10 px-4 text-sm',
      'backdrop-blur-sm border border-white/20',
      active
        ? 'bg-white/15 text-white shadow-[0_0_24px_rgba(255,255,255,0.45)]'
        : 'bg-white/10 text-white/85 hover:bg-white/12 hover:shadow-[0_0_18px_rgba(255,255,255,0.28)]',
      'focus:outline-none focus:ring-2 focus:ring-white/30',
    ].join(' ');

  // 👇 gradient inline style a partir de props.gradient
  const gradientStyle: React.CSSProperties = {
    background: `linear-gradient(${(gradient?.angle ?? 135)}deg, ${gradient?.from ?? '#000'} 0%, ${gradient?.via ?? '#171717'} 55%, ${gradient?.to ?? '#404040'} 100%)`,
    color: '#fff',
  };

  return (
    <section
      ref={(ref: HTMLElement | null) => { if (ref) connectRef(ref); }}
      className="w-full"
      style={{ background: bgColor, color: textColor }}
      data-section="vehicles"
      id="vehicles-section-2"
    >
      {/* CONTENEDOR CENTRAL */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        {/* ===== FULL-BLEED HEADER + FILTROS ===== */}
        <div className="relative left-1/2 right-1/2 -mx-[50vw] w-[100vw] max-w-[100vw] overflow-hidden">
          {/* ⬇️ Antes: tailwind bg-gradient-to-br ...  Ahora: estilo con gradient props */}
          <section className="text-white" style={gradientStyle}>
            <div className="max-w-[1200px] mx-auto">
              {/* Título */}
              <div className="text-center mb-5 pt-6 sm:pt-8">
                <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
                  <span dangerouslySetInnerHTML={htmlSeguro(resolvedTitle || t('site.vehicles.exploreStock'))} />
                </h2>
                {subtitle ? <p className="text-white/80 mt-2 text-sm sm:text-base" dangerouslySetInnerHTML={htmlSeguro(subtitle)} /> : null}
              </div>

              {/* Categorías – mobile */}
              <div className="md:hidden mb-5 -mx-4 px-4">
                <div
                  className="flex gap-4 px-4 pt-7 items-center overflow-x-auto snap-x snap-mandatory scroll-px-4 pb-1
                             [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden overscroll-x-contain"
                >
                  {typeCards.map(({ name, img }) => {
                    const isAll = name === 'Todos';
                    const active = typeSel === name || (isAll && typeSel === 'Todos');

                    return (
                      <button
                        type="button"
                        key={`mobile-${name}`}
                        onClick={() =>
                          setTypeSel(isAll ? 'Todos' : (typeSel === name ? 'Todos' : (name || 'Todos')))
                        }
                        className={[
                          'group flex flex-col items-center transition-transform duration-200 snap-start',
                          active ? '-translate-y-0.5' : 'hover:-translate-y-0.5',
                        ].join(' ')}
                        title={isAll ? etiquetaTodos : nombreTipo(name || '')}
                        style={{ minWidth: '6.75rem' }}
                      >
                        <div className="w-28 h-16 flex items-center justify-center rounded-xl bg-transparent">
                          {isAll ? (
                            <div className={allPillClasses(active, 'sm')}>
                              {etiquetaTodos.toUpperCase()}
                            </div>
                          ) : (
                            img && (
                              <img
                                src={img}
                                alt=""
                                aria-hidden="true"
                                className={pngGlow(active)}
                                onError={hideBrokenIcon}
                              />
                            )
                          )}
                        </div>
                        <span
                          className={[
                            'mt-2 text-[11px] tracking-widest',
                            active ? 'text-white' : 'text-white/80 group-hover:text-white/90',
                          ].join(' ')}
                        >
                          {(isAll ? etiquetaTodos : nombreTipo(name || '')).toUpperCase()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Categorías – desktop */}
              <div className="hidden md:flex flex-wrap justify-center gap-4 mb-6 px-4">
                {typeCards.map(({ name, img }) => {
                  const isAll = name === 'Todos';
                  const active = typeSel === name || (isAll && typeSel === 'Todos');

                  return (
                    <button
                      type="button"
                      key={`grid-${name}`}
                      onClick={() =>
                        setTypeSel(isAll ? 'Todos' : (typeSel === name ? 'Todos' : (name || 'Todos')))
                      }
                      className={[
                        'group flex flex-col items-center transition-transform duration-200',
                        active ? '-translate-y-1' : 'hover:-translate-y-1',
                      ].join(' ')}
                      title={isAll ? etiquetaTodos : nombreTipo(name || '')}
                    >
                      <div className="w-28 h-16 flex items-center justify-center rounded-xl bg-transparent">
                        {isAll ? (
                          <div className={allPillClasses(active, 'md')}>
                            {etiquetaTodos.toUpperCase()}
                          </div>
                        ) : (
                          img && (
                            <img
                              src={img}
                              alt=""
                              aria-hidden="true"
                              className={pngGlow(active)}
                              onError={hideBrokenIcon}
                            />
                          )
                        )}
                      </div>

                      <span
                        className={[
                          'mt-2 text-xs tracking-widest',
                          active ? 'text-white' : 'text-white/80 group-hover:text-white/90',
                        ].join(' ')}
                      >
                        {(isAll ? etiquetaTodos : nombreTipo(name || '')).toUpperCase()}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Barra de filtros */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 place-items-center px-4">
                <div className="w-full max-w-[240px]">
                  <label className="text-[11px] sm:text-sm text-white/90 block mb-1 text-center">{t('site.vehicles.carType')}</label>
                  <select
                    className="w-full border border-white/15 rounded-xl px-3 py-2 text-sm bg-white text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    value={typeSel}
                    onChange={(e) => setTypeSel(e.target.value)}
                  >
                    <option {...valorExplicito('Todos', etiquetaTodos)}>{etiquetaTodos}</option>
                    {!esBrasil && !esUsa && !types.some((t) => (t || '').toUpperCase() === 'MOTO, ATV Y UTV') && (
                      <option value="Moto, ATV y UTV">Moto, ATV y UTV</option>
                    )}
                    {types.map((t) => (
                      <option key={t} value={t}>{nombreTipo(t)}</option>
                    ))}
                  </select>
                </div>

                <div className="w-full max-w-[240px]">
                  <label className="text-[11px] sm:text-sm text-white/90 block mb-1 text-center">{t('site.vehicles.brand')}</label>
                  <select
                    className="w-full border border-white/15 rounded-xl px-3 py-2 text-sm bg-white text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    value={brandSel}
                    onChange={(e) => setBrandSel(e.target.value)}
                  >
                    <option {...valorExplicito('Todas', etiquetaTodas)}>{etiquetaTodas}</option>
                    {brands.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                <div className="w-full max-w-[240px]">
                  <label className="text-[11px] sm:text-sm text-white/90 block mb-1 text-center">{t('site.vehicles.transmission')}</label>
                  <select
                    className="w-full border border-white/15 rounded-xl px-3 py-2 text-sm bg-white text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    value={transSel}
                    onChange={(e) => setTransSel(e.target.value)}
                  >
                    <option {...valorExplicito('Todas', etiquetaTodas)}>{etiquetaTodas}</option>
                    {trans.map((t) => (
                      <option key={t} value={t}>{esUsa ? etiquetaCatalogo('transmission', t, client?.country, t) : t}</option>
                    ))}
                  </select>
                </div>

                <div className="w-full max-w-[240px]">
                  <label className="text-[11px] sm:text-sm text-white/90 block mb-1 text-center">{t('site.vehicles.fuel')}</label>
                  <select
                    className="w-full border border-white/15 rounded-xl px-3 py-2 text-sm bg-white text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    value={fuelSel}
                    onChange={(e) => setFuelSel(e.target.value)}
                  >
                    <option {...valorExplicito('Todos', etiquetaTodos)}>{etiquetaTodos}</option>
                    {fuels.map((f) => (
                      <option key={f} value={f}>{esUsa ? etiquetaCatalogo('fuel', f, client?.country, f) : f}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Sliders */}
              <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 place-items-center px-4 pb-8 md:pb-10">
                <div className="w-full max-w-[520px]">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/90">{t('site.vehicles.priceFromTo')}</span>
                    <span className="text-xs text-white/70">
                      {formatPrice(priceMin)} — {formatPrice(priceMax)}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 md:gap-4">
                    <input
                      type="range"
                      min={minMaxPrice.min}
                      max={minMaxPrice.max}
                      value={priceMin}
                      onChange={(e) => setPriceMin(Math.min(Number(e.target.value), priceMax))}
                      className="w-full accent-white/90 h-2"
                    />
                    <input
                      type="range"
                      min={minMaxPrice.min}
                      max={minMaxPrice.max}
                      value={priceMax}
                      onChange={(e) => setPriceMax(Math.max(Number(e.target.value), priceMin))}
                      className="w-full accent-white/90 h-2"
                    />
                  </div>
                </div>

                <div className="w-full max-w-[520px]">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/90">{t('site.vehicles.yearFromTo')}</span>
                    <span className="text-xs text-white/70">{yearMin} — {yearMax}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 md:gap-4">
                    <input
                      type="range"
                      min={minMaxYear.min}
                      max={minMaxYear.max}
                      value={yearMin}
                      onChange={(e) => setYearMin(Math.min(Number(e.target.value), yearMax))}
                      className="w-full accent-white/90 h-2"
                    />
                    <input
                      type="range"
                      min={minMaxYear.min}
                      max={minMaxYear.max}
                      value={yearMax}
                      onChange={(e) => setYearMax(Math.max(Number(e.target.value), yearMin))}
                      className="w-full accent-white/90 h-2"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
        {/* ===== /FULL-BLEED ===== */}

        {/* RESULTADOS */}
        <div className="mt-8 sm:mt-10">
          {loading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neutral-900" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center p-8 bg-gray-50 rounded-2xl border border-gray-200 max-w-xl mx-auto">
              <h3 className="text-lg font-medium text-neutral-900 mb-2">{t('site.vehicles.noResultsTitle')}</h3>
              <p className="text-neutral-600 mb-4">{t('site.vehicles.noResultsText')}</p>
              {hasActive && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setTypeSel('Todos');
                    setBrandSel('Todas');
                    setTransSel('Todas');
                    setFuelSel('Todos');
                    setPriceMin(minMaxPrice.min);
                    setPriceMax(minMaxPrice.max);
                    setYearMin(minMaxYear.min);
                    setYearMax(minMaxYear.max);
                  }}
                  className="rounded-xl text-white bg-neutral-900 hover:bg-neutral-800 border-neutral-800"
                >
                  {t('site.vehicles.removeAllFilters')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="mb-3 sm:mb-4 text-center">
                <p className="text-sm text-neutral-600">
                  <span className="font-semibold text-neutral-900">{filtered.length}</span>{' ' + textoEncontrados(filtered.length, t('site.vehicles.found'), client?.country)}
                </p>
              </div>
              <div className="max-w-[1200px] mx-auto">
                <VehicleList
                  vehicles={filtered as any}
                  columns={columns}
                  getStatusColor={(s: string) => getStatusColor(s)}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                  cardSettings={safeCardSettings}
                  newBadgeText={newBadgeText}
                  source="grid2"
                />
              </div>
            </>
          )}
        </div>

        {children}
      </div>
    </section>
  );
};

VehicleGrid2.craft = {
  displayName: 'VehicleGrid2',
  props: {
    title: 'Sarret Cars Boutique',
    subtitle: '',
    bgColor: '#ffffff',
    textColor: '#111827',
    columns: 3,
    showStatuses: ['Publicado', 'Reservado', 'Vendido'],
    gradient: { from: '#000000', via: '#171717', to: '#404040', angle: 135 }, // 👈 default del gradient
    cardSettings: [
      {
        cardBgColor: '#ffffff',
        cardBorderColor: '#e5e7eb',
        cardTextColor: '#1f2937',
        cardPriceColor: '#ffffff',
        cardButtonColor: '#224887',
        cardButtonTextColor: '#ffffff',
        detailsButtonText: 'Ver detalles',
        bannerPosition: 'right',
        pricePosition: 'overlay',
        featuresConfig: { feature1: 'category', feature2: 'year', feature3: 'fuel', feature4: 'mileage' },
      },
    ],
    newBadgeText: 'Recién publicado',
  },
  rules: { canDrag: () => true, canDrop: () => true, canMoveIn: () => true },
  isCanvas: true,
};
